/**
 * Khắc phục lỗi React crash khi người dùng sử dụng tính năng dịch của trình duyệt
 * (Google Translate, Chrome/Safari Translate, browser translation extensions).
 *
 * Cơ chế lỗi:
 * Google Translate thay thế hoặc bọc các Text node trong DOM bằng thẻ `<font><font>...</font></font>`.
 * Khi React thực hiện reconciliation (vd: khi submit form, nút chuyển sang trạng thái loading,
 * hiển thị thông báo lỗi, re-render, unmount...):
 *
 * 1. `parent.insertBefore(newNode, referenceNode)`:
 *    referenceNode (Text node cũ do React quản lý) đã bị Google Translate gỡ khỏi parent hoặc bọc
 *    vào trong thẻ <font>. Theo chuẩn DOM, nếu referenceNode không phải con trực tiếp của parent
 *    thì trình duyệt ném NotFoundError:
 *    "Failed to execute 'insertBefore' on 'Node': The node before which the new node is to be inserted is not a child of this node."
 *
 * 2. `parent.removeChild(child)`:
 *    child đã bị Google Translate gỡ khỏi parent. Trình duyệt ném NotFoundError:
 *    "Failed to execute 'removeChild' on 'Node': The node to be removed is not a child of this node."
 *
 * Các lỗi trên không thể bắt bằng try-catch thông thường trong component vì xảy ra trong commit
 * phase của React DOM, dẫn tới toàn bộ cây component bị unmount và hiện màn "Unexpected Application Error".
 *
 * Giải pháp:
 * Patch an toàn `Node.prototype.insertBefore` và `Node.prototype.removeChild`:
 * - Nếu referenceNode không phải con trực tiếp của `this`:
 *   + Nếu referenceNode là con cháu nằm trong một phần tử con của `this` (vd: Text node nằm trong
 *     <font> mà <font> là con của `this`), chèn trước phần tử tổ tiên đó.
 *   + Nếu referenceNode hoàn toàn bị tách rời khỏi `this` (parentNode là null hoặc không thuộc `this`),
 *     chèn với referenceNode = null (tương đương appendChild vào `this`) để đảm bảo newNode vẫn
 *     được gắn vào DOM mà không ném ngoại lệ.
 * - Nếu child cần remove không phải con của `this`:
 *   Bỏ qua an toàn và trả về child thay vì ném ngoại lệ.
 */

declare global {
  interface Node {
    __googleTranslateDOMPatchInstalled__?: boolean;
  }
}

export const installGoogleTranslateDOMPatch = (): void => {
  if (typeof Node === 'undefined' || !Node.prototype) {
    return;
  }

  if (Node.prototype.__googleTranslateDOMPatchInstalled__) {
    return;
  }

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      let ref: Node | null = referenceNode;
      while (ref && ref.parentNode && ref.parentNode !== this) {
        ref = ref.parentNode;
      }

      if (ref && ref.parentNode === this) {
        return originalInsertBefore.call(this, newNode, ref) as T;
      }

      return originalInsertBefore.call(this, newNode, null) as T;
    }

    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      return child;
    }

    return originalRemoveChild.call(this, child) as T;
  };

  Node.prototype.__googleTranslateDOMPatchInstalled__ = true;
};
